import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'How to migrate from Okta',
    metaDescription:
      'A realistic Okta-to-Monosign migration: export your app and user inventory, map SAML and OIDC configs, sync users, run both IdPs in parallel with per-app cutover, and migrate MFA enrollments last.',
    heroEyebrow: 'How-to · Migrate from Okta',
    heroTitle: ['Migrating off Okta?', 'Parallel-run it. App by app.'],
    heroBody:
      'An IdP migration is not a weekend project, and anyone who says otherwise has not done one. The good news: with both IdPs running in parallel and a per-app cutover, it is a sequence of small, reversible moves rather than one big-bang switch. Here is the honest version.',
    badges: ['Parallel IdP run', 'Per-app cutover', '~8 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the full docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From Okta tenant to a clean cutover.',
    stepsBody:
      'Six steps. The order matters: users first, apps in the middle, MFA last.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Export your app and user inventory',
        body: 'Pull the full list of applications, users, and assignments from the Okta admin console or API. Cross-check against sign-in logs: apps nobody has opened in months get retired, not migrated. The surviving list, with owners named per app, is your migration backlog.',
      },
      {
        title: 'Map SSO configs and groups',
        body: 'For each app, capture how it federates today: SAML metadata (entity IDs, ACS URLs, attribute statements) or OIDC client settings (client IDs, redirect URIs, scopes). Map Okta groups and app assignment rules to their Monosign equivalents so access policy survives the move intact.',
      },
      {
        title: 'Provision users into Monosign',
        body: 'Bring users across via SCIM or directory sync; if Okta was fed from Active Directory or another directory, point Monosign at the same source and it becomes the shared truth for both IdPs. Verify counts, group memberships, and a sample of attributes before touching any app.',
        hint: 'Passwords generally cannot be exported from Okta. Plan for directory-backed authentication or a first-login reset flow; decide this now, not during cutover.',
      },
      {
        title: 'Run both IdPs in parallel, cut over per app',
        body: 'Keep Okta serving everything while Monosign takes apps one at a time, starting with a low-stakes pilot. Register the app in Monosign with the mapped config, re-point the app, test with real users, then move to the next. At no point does anyone depend on a half-migrated IdP.',
        hint: 'Watch for hard-coded issuer URLs: apps that pin the Okta issuer, JWKS endpoint, or SAML entity ID in code or config will break silently. Grep your config repos for your okta.com domain before each cutover.',
      },
      {
        title: 'Migrate MFA enrollments last',
        body: 'MFA factors do not transfer between IdPs: TOTP seeds and passkeys are bound to the old tenant, so users will re-enroll their factors in Monosign. That is the honest reality of every IdP migration. Schedule it per user group with clear comms, and keep it last so users re-enroll exactly once.',
      },
      {
        title: 'Verify, then retire the Okta tenant',
        body: 'When Okta sign-in logs go quiet, do a final sweep: no active app assignments, no API tokens in use, no lingering SCIM connections. Then deactivate the apps, let the contract lapse, and archive the tenant export for your audit records.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Single Sign-On', description: 'SAML and OIDC federation in Monosign.', href: '/sign/sso' },
      { label: 'Directory & sources', description: 'AD, LDAP, and API sources as the shared truth.', href: '/sign/directory' },
      { label: 'Multi-Factor Authentication', description: 'The factors your users will re-enroll into.', href: '/sign/mfa' },
    ],
  },
  tr: {
    metaTitle: 'Okta’dan nasıl geçilir',
    metaDescription:
      'Gerçekçi bir Okta’dan Monosign’a geçiş: uygulama ve kullanıcı envanterini dışa aktarın, SAML ve OIDC yapılandırmalarını eşleyin, kullanıcıları senkronize edin, iki IdP’yi paralel çalıştırıp uygulama bazında geçin ve MFA kayıtlarını en sona bırakın.',
    heroEyebrow: 'Rehber · Okta’dan geçiş',
    heroTitle: ['Okta’dan mı ayrılıyorsunuz?', 'Paralel çalıştırın. Uygulama uygulama.'],
    heroBody:
      'Bir IdP geçişi hafta sonu projesi değildir; aksini söyleyen, hiç yapmamıştır. İyi haber: iki IdP paralel çalışırken ve geçiş uygulama bazında yapılırken, süreç tek büyük bir anahtar değişimi değil, küçük ve geri alınabilir adımlar dizisidir. İşte dürüst versiyonu.',
    badges: ['Paralel IdP çalıştırma', 'Uygulama bazlı geçiş', '~8 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'Okta tenant’ından temiz bir geçişe.',
    stepsBody:
      'Altı adım. Sıra önemli: önce kullanıcılar, ortada uygulamalar, en sonda MFA.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: 'Uygulama ve kullanıcı envanterini dışa aktarın',
        body: 'Okta yönetim konsolundan veya API’den tüm uygulama, kullanıcı ve atama listesini çekin. Oturum açma loglarıyla karşılaştırın: aylardır kimsenin açmadığı uygulamalar taşınmaz, emekliye ayrılır. Kalan liste, her uygulamaya bir sahip atanmış hâliyle, geçiş planınızdır.',
      },
      {
        title: 'SSO yapılandırmalarını ve grupları eşleyin',
        body: 'Her uygulamanın bugün nasıl federasyon yaptığını kaydedin: SAML metadata (entity ID’ler, ACS URL’leri, öznitelik ifadeleri) veya OIDC istemci ayarları (client ID’ler, redirect URI’lar, scope’lar). Okta gruplarını ve atama kurallarını Monosign karşılıklarına eşleyin ki erişim politikası geçişten sağlam çıksın.',
      },
      {
        title: 'Kullanıcıları Monosign’a aktarın',
        body: 'Kullanıcıları SCIM veya dizin senkronizasyonu ile taşıyın; Okta, Active Directory ya da başka bir dizinden besleniyorduysa Monosign’ı aynı kaynağa yönlendirin; kaynak iki IdP için de ortak doğruluk hâline gelir. Herhangi bir uygulamaya dokunmadan önce sayıları, grup üyeliklerini ve örnek öznitelikleri doğrulayın.',
        hint: 'Parolalar genellikle Okta’dan dışa aktarılamaz. Dizin destekli kimlik doğrulama veya ilk girişte parola sıfırlama akışı planlayın; bu karar geçiş anında değil, şimdi verilmeli.',
      },
      {
        title: 'İki IdP’yi paralel çalıştırın, uygulama bazında geçin',
        body: 'Monosign uygulamaları teker teker devralırken Okta her şeye hizmet vermeye devam etsin; düşük riskli bir pilotla başlayın. Uygulamayı eşlediğiniz yapılandırmayla Monosign’a kaydedin, uygulamayı yönlendirin, gerçek kullanıcılarla test edin, sonra sıradakine geçin. Hiçbir an kimse yarım taşınmış bir IdP’ye bağımlı kalmaz.',
        hint: 'Sabit kodlanmış issuer URL’lerine dikkat: Okta issuer’ını, JWKS endpoint’ini veya SAML entity ID’sini kodda ya da konfigürasyonda sabitleyen uygulamalar sessizce bozulur. Her geçişten önce konfigürasyon depolarınızda okta.com alan adınızı aratın.',
      },
      {
        title: 'MFA kayıtlarını en son taşıyın',
        body: 'MFA faktörleri IdP’ler arasında aktarılamaz: TOTP tohumları ve passkey’ler eski tenant’a bağlıdır; kullanıcılar faktörlerini Monosign’da yeniden kaydedecektir. Bu, her IdP geçişinin dürüst gerçeğidir. Yeniden kaydı kullanıcı grubu bazında, net bir duyuruyla planlayın ve en sona bırakın ki herkes yalnızca bir kez kayıt yenilesin.',
      },
      {
        title: 'Doğrulayın, sonra Okta tenant’ını emekliye ayırın',
        body: 'Okta oturum açma logları sessizleştiğinde son bir tarama yapın: aktif uygulama ataması, kullanımda API token’ı ve unutulmuş SCIM bağlantısı kalmasın. Ardından uygulamaları devre dışı bırakın, sözleşmeyi sonlandırın ve tenant dışa aktarımını denetim kayıtlarınız için arşivleyin.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Tek Oturum Açma (SSO)', description: "Monosign'da SAML ve OIDC federasyonu.", href: '/sign/sso' },
      { label: 'Dizin ve kaynaklar', description: 'Ortak doğruluk kaynağı olarak AD, LDAP ve API kaynakları.', href: '/sign/directory' },
      { label: 'Çok Faktörlü Kimlik Doğrulama', description: 'Kullanıcılarınızın yeniden kaydolacağı faktörler.', href: '/sign/mfa' },
    ],
  },
}

type Props = { locale: string }

export default function MigrateFromOktaPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/migrate-from-okta"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/saml-2.0-integrations' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/saml-2.0-integrations"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
